import React from 'react';
import { useAuth } from '../../context/AuthContext';
import RoleTemplate, { RoleTemplateProps } from './RoleTemplate';
import { Navigate } from 'react-router-dom';

interface RoleGuardProps {
  roles?: string; // comma delimited string, e.g. "Admin,Manager"
  children: React.ReactNode;
  allow?: () => boolean;
  redirectOnDeny?: boolean; // new prop
}

const RoleGuard: React.FC<RoleGuardProps> = ({ roles, children, allow, redirectOnDeny = false }) => {
  const { user } = useAuth();

  // Gather RoleTemplate children if present
  const templates = React.Children.toArray(children).filter(
    (child) => React.isValidElement(child) && (child.type === RoleTemplate)
  ) as React.ReactElement<RoleTemplateProps>[];

  // If RoleTemplate children exist, use them for per-role rendering
  if (Array.isArray(templates) && templates.length > 0) {
    for (const template of templates) {
      const roleProp = (template.props.role || '').toLowerCase();
      const allowFn = template.props.allow;
      if (!roleProp) {
        if (!allowFn || allowFn()) {
          return <>{template.props.children}</>;
        }
        continue;
      }
      // Support multiple roles in template: "Admin,Manager"
      const allowedRoles = roleProp.split(',').map(r => r.trim()).filter(Boolean);
      if (user && user.role && allowedRoles.includes(user.role.toLowerCase())) {
        if (!allowFn || allowFn()) {
          return <>{template.props.children}</>;
        }
      }
    }
    return redirectOnDeny ? <Navigate to="/" replace /> : null;
  }

  // If no roles prop or it's empty, allow anonymous (always render)
  if (!roles || roles.trim() === '') {
    if (!allow || allow()) {
      return <>{children}</>;
    }
    return redirectOnDeny ? <Navigate to="/" replace /> : null;
  }

  // Otherwise, use roles prop for allowed roles
  if (!user) return redirectOnDeny ? <Navigate to="/" replace /> : null;
  const allowedRoles = roles.split(',').map(r => r.trim().toLowerCase());
  const userRole = (user.role || '').toLowerCase();
  if (!allowedRoles.includes(userRole)) return redirectOnDeny ? <Navigate to="/" replace /> : null;
  // Support allow prop on RoleGuard itself
  if (allow && !allow()) return redirectOnDeny ? <Navigate to="/" replace /> : null;
  return <>{children}</>;
};

export default RoleGuard;
